/**
 * Slash-command registry
 *
 * Defines the top-level verbs and their sub-commands.
 * /ai  — AI assistant actions (create, modify, explain, improve, fix)
 * /add — Insert-code actions (built dynamically from the Command[] registry)
 *
 * Execute functions are injected at runtime when building the slash-command list.
 */

import type React from 'react';

export interface SlashSubcommand {
  /** Unique key within the verb, e.g. "create", "cube" */
  name: string;
  /** Short, human-readable label */
  label: string;
  /** Inline argument hint, e.g. "<prompt>" or "[size]" */
  argLabel?: string;
  /** One-line description shown in the dropdown */
  description: string;
  /** Whether an argument is required to execute (defaults false) */
  argRequired?: boolean;
  /** Whether this sub-command accepts a free-text argument at all */
  hasArg?: boolean;
  /** Runtime execute function — injected by SlashCommandList */
  execute: (arg?: string) => void;
  /** Icon component */
  icon?: React.ComponentType<{ className?: string }>;
}

export interface SlashVerb {
  /** e.g. "ai", "add" */
  verb: string;
  /** Display label */
  label: string;
  /** One-line description */
  description: string;
  /** Icon component */
  icon?: React.ComponentType<{ className?: string }>;
  /** Sub-commands for this verb */
  subcommands: SlashSubcommand[];
}

// ─── /ai sub-commands (execute stubs — wired in at runtime) ───────────────────

export const AI_SUBCOMMANDS: Omit<SlashSubcommand, 'execute'>[] = [
  {
    name: 'create',
    label: 'create',
    argLabel: '<prompt>',
    description: 'Generate new SCAD code from scratch',
    hasArg: true,
    argRequired: true,
  },
  {
    name: 'modify',
    label: 'modify',
    argLabel: '<prompt>',
    description: 'Modify the current code based on a prompt',
    hasArg: true,
    argRequired: true,
  },
  {
    name: 'explain',
    label: 'explain',
    description: 'Explain the current or selected code',
    hasArg: false,
  },
  {
    name: 'improve',
    label: 'improve',
    description: 'Improve / refactor the current code',
    hasArg: false,
  },
  {
    name: 'fix',
    label: 'fix',
    description: 'Fix compile errors in the current code',
    hasArg: false,
  },
];

// ─── Helper to get the best ghost-text completion for current input ────────────

/**
 * Returns the ghost-text suffix to display after the cursor.
 * e.g. input="/ai cr" → ghost=" eate <prompt>"  (the rest of the completion)
 */
export function getGhostSuffix(
  input: string,
  verbs: SlashVerb[],
): string {
  if (!input.startsWith('/')) return '';

  const withoutSlash = input.slice(1);
  const parts = withoutSlash.split(' ');

  // Phase: verb only (no space yet)
  if (parts.length === 1) {
    const verbPartial = parts[0].toLowerCase();
    const matched = verbs.find((v) => v.verb.startsWith(verbPartial));
    if (!matched) return '';
    if (matched.verb === verbPartial) {
      // Exact verb match — suggest first sub
      const firstSub = matched.subcommands[0];
      if (!firstSub) return '';
      const subSuffix = firstSub.argLabel ? ` ${firstSub.argLabel}` : '';
      return ` ${firstSub.name}${subSuffix}`;
    }
    // Partial verb match — complete the verb + first sub
    const verbSuffix = matched.verb.slice(verbPartial.length);
    const firstSub = matched.subcommands[0];
    if (!firstSub) return verbSuffix;
    const subSuffix = firstSub.argLabel ? ` ${firstSub.argLabel}` : '';
    return `${verbSuffix} ${firstSub.name}${subSuffix}`;
  }

  const verbStr = parts[0].toLowerCase();
  const verb = verbs.find((v) => v.verb === verbStr);
  if (!verb) return '';

  // Phase: sub (one space after verb)
  if (parts.length === 2) {
    const subPartial = parts[1].toLowerCase();
    // If sub is empty, suggest first sub
    if (!subPartial) {
      const firstSub = verb.subcommands[0];
      if (!firstSub) return '';
      return firstSub.argLabel ? `${firstSub.name} ${firstSub.argLabel}` : firstSub.name;
    }
    const matched = verb.subcommands.find((s) => s.name.startsWith(subPartial));
    if (!matched) return '';
    if (matched.name === subPartial) {
      // Exact sub match — suggest arg label if any
      return matched.argLabel ? ` ${matched.argLabel}` : '';
    }
    const subSuffix = matched.name.slice(subPartial.length);
    return matched.argLabel ? `${subSuffix} ${matched.argLabel}` : subSuffix;
  }

  // Phase: arg — no ghost text for free-text
  return '';
}
